import { memo } from 'react';
import { User, Check } from 'lucide-react';
import { DepositIncomingCard } from '@/supabase/deposit/types/deposit';
import { cn } from '@/lib/utils';

interface DepositIncomingCardViewProps {
  card: DepositIncomingCard;
  onConfirm: (card: DepositIncomingCard) => void;
  onReject?: (card: DepositIncomingCard) => void; // ✗ 무시 → /deposits 수동 큐로
  onUndo?: (card: DepositIncomingCard) => void; // 되돌리기 → 확정 취소(상태+다리학습 원복)
  confirming: boolean; // true while this card's confirm/reject is in flight → disable buttons
  justArrived?: boolean; // 실시간으로 방금 도착 → flash + "방금" 배지
  confirmed?: boolean; // 방금 확정됨 → '입금 확정됨 · 되돌리기' 5초 인라인 표시
}

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

function DepositIncomingCardViewBase({
  card,
  onConfirm,
  onReject,
  onUndo,
  confirming,
  justArrived = false,
  confirmed = false,
}: DepositIncomingCardViewProps) {
  // 확정 직후: 토스트 대신 카드 자리에 '입금 확정됨 · 되돌리기'(5초, 하단 카운트다운 바 후 자동 소멸).
  if (confirmed) {
    return (
      <div role="status" className="deposit-chip-in relative px-2 py-1.5 rounded-md border border-green-300 bg-green-50 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-green-700">
            <Check className="w-3.5 h-3.5" strokeWidth={3} aria-hidden />
            입금 확정됨
          </span>
          <button
            type="button"
            aria-label={`${won(card.amount)} ${card.depositorName} 입금 확정 되돌리기`}
            className="h-6 px-2 text-xs rounded bg-white text-slate-700 border border-slate-300 font-medium hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1"
            onClick={() => onUndo?.(card)}
          >
            되돌리기
          </button>
        </div>
        <span className="deposit-countdown absolute bottom-0 left-0 h-[2px] w-full bg-green-400" aria-hidden />
      </div>
    );
  }
  return (
    <div
      className={cn(
        'relative px-1.5 py-1 rounded-md border border-blue-300 bg-blue-100 transition-colors',
        justArrived && 'deposit-flash'
      )}
    >
      <div className="flex items-center min-w-0">
        <User className="w-3 h-3 mr-1.5 flex-shrink-0 text-indigo-500" aria-hidden />
        <span className="min-w-0 truncate text-xs font-medium text-gray-700">
          {card.nickname}
        </span>
        {/* 입금자 실명 — 닉↔실명 다리(동명/동액 입금 구분). 닉네임과 같으면 생략.
            실명은 보통 짧아 shrink-0; 긴 닉네임이 먼저 truncate된다. */}
        {card.depositorName && card.depositorName !== card.nickname && (
          <span className="ml-1 flex-shrink-0 text-[11px] text-blue-700/70">
            {card.depositorName}
          </span>
        )}
        {justArrived && (
          <span className="ml-1 flex-shrink-0 text-[10px] font-semibold text-blue-600">방금</span>
        )}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1 min-w-0 text-[11px] text-blue-800 tabular-nums">
          {won(card.amount)} · 금액 일치
          {card.remainingAmount !== null && card.remainingAmount > 0 && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
              잔금 입금
            </span>
          )}
        </span>
        <div className="flex items-center gap-1.5">
          {onReject && (
            <button
              type="button"
              aria-label={`${won(card.amount)} ${card.depositorName} 입금은 ${card.nickname} 주문이 아님(제외)`}
              className="h-6 px-2 text-xs rounded bg-white text-red-600 border border-red-200 font-medium hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={confirming}
              onClick={() => onReject(card)}
            >
              제외
            </button>
          )}
          <button
            type="button"
            aria-label={`${won(card.amount)} ${card.depositorName} 입금을 ${card.nickname} 주문 입금완료로 확인`}
            className="h-6 px-2.5 text-xs rounded bg-blue-600 text-white font-medium hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={confirming}
            onClick={() => onConfirm(card)}
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}

export const DepositIncomingCardView = memo(DepositIncomingCardViewBase);
